import { useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { editionReadUrl, regionUrl, type BlockRow, type Region } from '../../lib/api'
import { useSession } from '../../lib/session'
import { Confidence, CropPlaceholder, PanelLabel, Segmented, TypeBadge, typeOf } from '../Page'
import { CopyButton } from './Popovers'
import { PassageText, DoubtNote } from './Text'
import {
  CITATION_LABELS,
  CITATION_STYLES,
  citationFor,
  type CitationFacts,
  type CitationStyle,
} from './citation'
import {
  LOW_CONFIDENCE,
  bboxToIiif,
  paragraphsOf,
  readingPassages,
  type Passage,
} from './model'

/**
 * One item, read rather than inspected.
 *
 * The crop comes first because the claim the archive makes is that this text
 * was printed on that sheet at those coordinates, and the crop is the proof. A
 * researcher who cannot verify a passage against the original page has nothing
 * worth quoting, so "See it on the page" is the primary action and the
 * citation is on the screen rather than behind a menu.
 */

export interface ReadingRecord {
  kind: string
  title: string | null
  subtitle: string | null
  byline: string | null
  pubDate: string | null
  pageLabel: string | null
  pageId: string | null
  region: Region | null
  route: string | null
  engine: string | null
  confidence: number | null
  wordCount: number | null
  isWire: boolean
  wireService: string | null
  columnsLabel: string | null
  publication: string | null
  correctionSource: string | null
  textRaw: string | null
  textCorrected: string | null
  blocks: BlockRow[]
  citation: Record<string, string | null> | null
  permalink: string | null
  needsReview: boolean
  jumpTo: string | null
  /** For the issue PDF, opened at this record's page. */
  editionId?: string | null
  pageNumber?: number | null
}

function passagesFrom(record: ReadingRecord, corrected: boolean): Passage[] {
  // An editor's text is the article's, not any one block's, so it is read
  // whole; otherwise the blocks give the paragraphs as printed.
  const edited = corrected && record.correctionSource === 'editor' && record.textCorrected
  const withText = record.blocks.some((block) => (block.raw_text ?? '').trim())
  if (withText && !edited) return readingPassages(record.blocks, corrected)
  const text = (corrected ? record.textCorrected || record.textRaw : record.textRaw) ?? ''
  return paragraphsOf(text).map((paragraph, index) => ({
    id: `p${index}`,
    text: paragraph,
    confidence: record.confidence,
    words: null,
  }))
}

export function ReadingView({
  record,
  productName,
  backHref,
  actions,
}: {
  record: ReadingRecord
  productName: string
  backHref: string | null
  actions?: ReactNode
}) {
  const { can } = useSession()
  // A person's correction is shown by default; a machine's is one click away.
  const [corrected, setCorrected] = useState(record.correctionSource === 'editor')
  const [style, setStyle] = useState<CitationStyle>('mla')

  const passages = passagesFrom(record, corrected)
  const hasCorrections =
    !!record.textCorrected || record.blocks.some((block) => block.corrected_text)
  const type = typeOf(record.kind)
  const low =
    record.confidence !== null && record.confidence !== undefined && record.confidence < LOW_CONFIDENCE

  const facts: CitationFacts = {
    author: record.byline,
    headline: record.title,
    publication: record.publication,
    date: record.pubDate,
    pageLabel: record.pageLabel,
    columns: record.columnsLabel,
    permalink: record.permalink
      ? `${window.location.origin}${record.permalink.replace(/^https?:\/\/[^/]+/, '')}`
      : window.location.href,
    accessed: new Date().toISOString().slice(0, 10),
    wireService: record.wireService,
    productName,
  }
  const citation = citationFor(style, record.citation, facts)

  const pageHref = record.pageId
    ? `/page/${record.pageId}${record.region ? `?r=${bboxToIiif(record.region)}` : ''}`
    : null

  return (
    <article className="mx-auto w-full max-w-[860px] px-4 py-6 md:px-8 md:py-10">
      <div className="mb-3 flex flex-wrap items-center gap-2.5">
        <TypeBadge type={record.kind} />
        <span className="chrome">{type.name}</span>
        <span className="font-mono text-[10.5px] text-ink2">
          {[record.pubDate, record.pageLabel ? `p. ${record.pageLabel}` : null, record.columnsLabel]
            .filter(Boolean)
            .join(' · ')}
        </span>
        {record.needsReview && (
          <span className="ml-auto font-mono text-[10px] tracking-[0.06em] text-mark">
            REVIEW QUEUED
          </span>
        )}
      </div>

      <h1 className="text-[38px] leading-[1.04]">{record.title ?? 'Untitled'}</h1>
      {record.subtitle && (
        <p className="mt-2.5 text-[19px] italic leading-[1.35] text-ink2">{record.subtitle}</p>
      )}
      <p className="mt-2.5 text-[15px] text-ink2">
        {/* A photograph's credit and an advertisement's "advertiser
            unresolved" are not bylines, so they are not prefixed as if they
            were somebody's name. */}
        {record.byline
          ? record.kind === 'article' || record.kind === 'notice'
            ? `By ${record.byline.replace(/^by\s+/i, '')}`
            : record.byline
          : 'Unattributed'}
        {record.publication ? ` · ${record.publication}` : ''}
        {record.isWire && record.wireService ? ` · ${record.wireService}` : ''}
      </p>

      {/* The crop: this passage as it sits on the printed sheet. */}
      <figure className="mt-6">
        {record.pageId && record.region ? (
          /* The crop is shown at full width and the frame is bounded rather
             than the image: a column running the depth of a broadsheet would
             otherwise be scaled down to an illegible strip, or would push the
             text, the citation and the provenance several screens down. The
             frame scrolls, and the page itself is one click away. */
          <div className="max-h-[460px] overflow-y-auto bg-well">
            <img
              src={regionUrl(record.pageId, record.region, 1200)}
              alt={`${record.title ?? 'This region'} as it appeared on page ${
                record.pageLabel ?? ''
              } of ${record.publication ?? 'the paper'}, ${record.pubDate ?? ''}`}
              className="block w-full"
            />
          </div>
        ) : (
          <CropPlaceholder
            className="h-[220px] w-full"
            lines={[
              'NO CROP',
              record.pageId ? 'THIS RECORD CARRIES NO REGION' : 'THIS RECORD NAMES NO PAGE',
            ]}
          />
        )}
        <figcaption className="mt-2 flex flex-wrap items-center gap-3 font-mono text-[10px] text-ink2">
          <span>
            {record.pageLabel ? `PAGE ${record.pageLabel}` : 'PAGE UNKNOWN'}
            {record.region ? ` · ${bboxToIiif(record.region)}` : ''}
          </span>
          <span className="ml-auto">
            {[record.route ? `ROUTE ${record.route}` : null, record.engine?.toUpperCase()]
              .filter(Boolean)
              .join(' · ')}
          </span>
        </figcaption>
      </figure>

      <div className="mt-5 flex flex-wrap items-center gap-2">
        {pageHref && (
          <Link to={pageHref} className="btn-primary no-underline">
            See it on the page
          </Link>
        )}
        {backHref && (
          <Link to={backHref} className="btn-secondary no-underline">
            Back to the results
          </Link>
        )}
        {record.editionId && record.pageNumber && can('content.view_page_image') && (
          <a
            href={`${editionReadUrl(record.editionId)}#page=${record.pageNumber}`}
            target="_blank"
            rel="noopener"
            className="btn-secondary no-underline"
          >
            Read in the issue PDF
          </a>
        )}
        {actions}
        {record.jumpTo && (
          <span className="font-mono text-[10.5px] text-ink2">
            CONTINUES ON {record.jumpTo.toUpperCase()}
          </span>
        )}
      </div>

      <div className="mt-8 flex flex-wrap items-center gap-3 border-t border-n300 pt-4">
        <Segmented
          ariaLabel="Which text to show"
          value={corrected ? 'corrected' : 'recognised'}
          onChange={(next) => setCorrected(next === 'corrected')}
          options={[
            { value: 'recognised', label: 'Recognised' },
            {
              value: 'corrected',
              label: 'Corrected',
              disabled: !hasCorrections,
              title: hasCorrections
                ? 'A labelled reading aid, attributed to its source'
                : 'Nothing has been corrected in this record',
            },
          ]}
        />
        {!hasCorrections && (
          <span className="font-mono text-[10px] text-ink3">NO CORRECTIONS RECORDED</span>
        )}
        <span className="ml-auto flex items-center gap-3">
          {record.wordCount ? (
            <span className="font-mono text-[10.5px] text-ink2">{record.wordCount} WORDS</span>
          ) : null}
          <Confidence value={record.confidence} engine={record.engine} />
        </span>
      </div>

      <div className="mt-4">
        {passages.length ? (
          <>
            <PassageText passages={passages} size={16.5} />
            <DoubtNote passages={passages} reviewQueued={record.needsReview} />
          </>
        ) : (
          <p className="text-[15px] text-ink2">
            No text was extracted for this record. The crop above is still the evidence, and the
            coordinates below still address it.
          </p>
        )}
        {corrected && (
          <p className="ident mt-3 text-mark">
            Corrected by {record.correctionSource ?? 'a correction pass'}. The recognised text is
            the authoritative record of what the paper printed and is never overwritten.
          </p>
        )}
      </div>

      <section className="mt-10">
        <PanelLabel note={record.citation?.mla ? 'PUBLISHER IMPRINT' : 'COMPOSED FROM THE RECORD'}>
          Cite this
        </PanelLabel>
        <div className="flex flex-wrap items-center gap-2 pb-2 font-mono text-[10px] tracking-[0.06em]">
          {CITATION_STYLES.map((option) => (
            <button
              key={option}
              type="button"
              aria-pressed={option === style}
              onClick={() => setStyle(option)}
              className={option === style ? 'uppercase text-ink' : 'uppercase text-accent'}
            >
              {CITATION_LABELS[option]}
            </button>
          ))}
        </div>
        {/* MLA, Chicago and APA are prose and are set in the reading serif.
            BibTeX and RIS are machine formats, so they take the mono. */}
        {style === 'bibtex' || style === 'ris' ? (
          <pre className="card overflow-x-auto whitespace-pre-wrap px-3.5 py-3 font-mono text-[12.5px] leading-relaxed">
            {citation}
          </pre>
        ) : (
          <p className="card whitespace-pre-wrap px-3.5 py-3 text-[15px] leading-normal">
            {citation}
          </p>
        )}
        <div className="mt-2.5 flex gap-2">
          <CopyButton value={citation} label="Copy" />
          {record.permalink && (
            <CopyButton
              value={`${window.location.origin}${record.permalink.replace(/^https?:\/\/[^/]+/, '')}`}
              label="Copy permalink"
              primary={false}
            />
          )}
        </div>
      </section>

      <footer className="mt-10 border-t border-n300 pt-4 font-mono text-[10.5px] leading-[1.8] text-ink2">
        <div>
          {[
            record.route ? `ROUTE ${record.route}` : null,
            record.engine?.toUpperCase(),
            record.confidence !== null && record.confidence !== undefined
              ? `${(record.confidence * 100).toFixed(1)}% CONFIDENCE`
              : null,
            record.blocks.length ? `${record.blocks.length} BLOCKS` : null,
          ]
            .filter(Boolean)
            .join(' · ')}
        </div>
        <p className="mt-2 max-w-[76ch] font-serif text-[14.5px] leading-normal">
          The recognised text is what the paper printed and is never overwritten. Corrected text,
          where any exists, is a labelled reading aid attributed to its source.
          {low
            ? ' This record read below 85%, so it is queued for human review and the figure above is set in magenta.'
            : ''}
        </p>
      </footer>
    </article>
  )
}
